import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { createShare, uploadShareNda, updateShare } from '../../services/shareService';
import { getClientsOnce } from '../../services/clientsService';
import { queuePocketDealEmail } from '../../services/pocketDealEmailService';
import { normalizeEmail } from '../../services/userEmailService';

/**
 * @param {object} props
 * @param {string} props.propertyId
 * @param {import('firebase/auth').User} props.currentUser
 * @param {object} props.userProfile
 * @param {string} props.language
 * @param {function} props.t
 * @param {boolean} props.shareForCession - NDA / parcours cession uniquement si true (aligné sur ShareModal).
 * @param {boolean} props.includeNda - NDA required on share when true (file must be set via ndaRef)
 * @param {boolean} [props.requireNdaOnShare] - When true, validate/upload NDA (owned diligence or cession+NDA)
 * @param {() => object} props.getVipShareOpts - returns opts for createShare (openToCession, sharedByBroker, etc.)
 * @param {import('react').RefObject<{ ndaFile: File|null, selectedMyFileRef: object|null }>} props.ndaRef
 * @param {(propertyId: string) => void} [props.onShared]
 * @param {() => void} [props.onClose]
 * @param {(msg: string) => void} props.setSuccess
 * @param {(msg: string) => void} props.setError
 * @param {boolean} props.loading
 * @param {(v: boolean) => void} props.setLoading
 * @param {object|null} [props.vipDraft] - Préremplissage (ex. renvoi campagne)
 */
export default function ShareModalVipPanel({
  propertyId,
  currentUser,
  userProfile,
  language,
  t,
  shareForCession = false,
  includeNda = false,
  requireNdaOnShare = false,
  getVipShareOpts,
  ndaRef,
  onShared,
  onClose,
  setSuccess,
  setError,
  loading,
  setLoading,
  vipDraft = null,
}) {
  const [vipClients, setVipClients] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [vipSubject, setVipSubject] = useState('');
  const [vipPreheader, setVipPreheader] = useState('');
  const [vipIntro, setVipIntro] = useState('');
  const [vipIncludeDescription, setVipIncludeDescription] = useState(true);
  const [vipIncludeImage, setVipIncludeImage] = useState(true);
  const [vipIncludeMetrics, setVipIncludeMetrics] = useState(true);
  const [vipIncludeScenario, setVipIncludeScenario] = useState(false);
  const [vipScenarioId, setVipScenarioId] = useState('');
  const [vipEmailFr, setVipEmailFr] = useState(true);
  const [vipEmailEn, setVipEmailEn] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) return;
    getClientsOnce(currentUser.uid).then(setVipClients).catch(() => setVipClients([]));
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!vipDraft) return;
    setVipSubject(vipDraft.subjectLine != null ? String(vipDraft.subjectLine) : '');
    setVipPreheader(vipDraft.preheader != null ? String(vipDraft.preheader) : '');
    setVipIntro(vipDraft.introText != null ? String(vipDraft.introText) : '');
    if (vipDraft.includeDescription !== undefined) setVipIncludeDescription(!!vipDraft.includeDescription);
    if (vipDraft.includeImage !== undefined) setVipIncludeImage(!!vipDraft.includeImage);
    if (vipDraft.includeMetrics !== undefined) setVipIncludeMetrics(!!vipDraft.includeMetrics);
    if (vipDraft.includeScenario !== undefined) setVipIncludeScenario(!!vipDraft.includeScenario);
    setVipScenarioId(vipDraft.scenarioId != null && vipDraft.scenarioId !== '' ? String(vipDraft.scenarioId) : '');
    if (vipDraft.emailLanguages === 'both') {
      setVipEmailFr(true);
      setVipEmailEn(true);
    } else if (vipDraft.emailLanguages === 'en') {
      setVipEmailFr(false);
      setVipEmailEn(true);
    } else if (vipDraft.emailLanguages === 'fr') {
      setVipEmailFr(true);
      setVipEmailEn(false);
    } else if (vipDraft.locale === 'en') {
      setVipEmailFr(false);
      setVipEmailEn(true);
    } else {
      setVipEmailFr(true);
      setVipEmailEn(false);
    }
  }, [vipDraft, propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    getDocs(collection(firestore, 'properties', propertyId, 'scenarios'))
      .then((snap) => {
        if (cancelled) return;
        setScenarios(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {
        if (!cancelled) setScenarios([]);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const recipients = vipClients.filter((c) => c.sendPocketDeals === true && normalizeEmail(c.email));

  const handleVipSend = async () => {
    if (!currentUser?.uid || !propertyId) return;
    if (requireNdaOnShare || (shareForCession && includeNda)) {
      const ndaFile = ndaRef?.current?.ndaFile;
      const selectedRef = ndaRef?.current?.selectedMyFileRef;
      if (!ndaFile && !selectedRef?.storagePath) {
        setError(t('shareModal.ndaRequired'));
        return;
      }
    }
    if (recipients.length === 0) {
      setError(t('shareModal.vipNoRecipients'));
      return;
    }
    if (!vipEmailFr && !vipEmailEn) {
      setError(t('shareModal.vipEmailLangRequired'));
      return;
    }
    const emailLanguages = vipEmailFr && vipEmailEn ? 'both' : vipEmailEn ? 'en' : 'fr';
    setLoading(true);
    setError('');
    const campaignId = `pc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const base = getVipShareOpts();
    const senderName = userProfile?.displayName || currentUser.displayName || '';
    const locale = language === 'en' ? 'en' : 'fr';
    try {
      for (const c of recipients) {
        const { token, url } = await createShare(currentUser.uid, {
          ...base,
          pocketDealMode: true,
          pocketRecipientClientId: c.id,
          pocketCampaignId: campaignId,
          scenarioId: vipIncludeScenario && vipScenarioId ? vipScenarioId : null,
        });
        const ndaFile = ndaRef?.current?.ndaFile;
        const selectedRef = ndaRef?.current?.selectedMyFileRef;
        if ((requireNdaOnShare || (shareForCession && includeNda)) && ndaFile && !selectedRef?.storagePath) {
          const fileRef = await uploadShareNda(token, ndaFile);
          await updateShare(token, { ndaFileRef: fileRef });
        }
        const shareUrl = url.includes('?') ? `${url}&pc=${encodeURIComponent(c.id)}` : `${url}?pc=${encodeURIComponent(c.id)}`;
        await queuePocketDealEmail({
          brokerUid: currentUser.uid,
          to: c.email,
          shareToken: token,
          shareUrl,
          clientId: c.id,
          campaignId,
          senderDisplayName: senderName,
          subjectLine: vipSubject.trim() || t('shareModal.vipDefaultSubject'),
          preheader: vipPreheader.trim(),
          introText: vipIntro.trim(),
          includeDescription: vipIncludeDescription,
          includeImage: vipIncludeImage,
          includeMetrics: vipIncludeMetrics,
          includeScenario: vipIncludeScenario,
          scenarioId: vipIncludeScenario && vipScenarioId ? vipScenarioId : null,
          locale,
          emailLanguages,
        });
      }
      setSuccess(t('shareModal.vipQueued').replace('{n}', String(recipients.length)));
      onShared?.(propertyId);
      setTimeout(() => onClose?.(), 2200);
    } catch (e) {
      setError(e?.message || t('share.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">{t('shareModal.vipHelp')}</p>
      <p className="text-sm font-medium text-gray-800">
        {t('shareModal.vipRecipientsCount').replace('{count}', String(recipients.length))}
      </p>
      <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-3 space-y-2">
        <p className="text-sm font-medium text-gray-800">{t('shareModal.vipEmailLanguages')}</p>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={vipEmailFr}
            onChange={(e) => setVipEmailFr(e.target.checked)}
            disabled={loading}
          />
          {t('shareModal.vipEmailLangFr')}
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={vipEmailEn}
            onChange={(e) => setVipEmailEn(e.target.checked)}
            disabled={loading}
          />
          {t('shareModal.vipEmailLangEn')}
        </label>
        <p className="text-xs text-gray-600">{t('shareModal.vipEmailLanguagesHint')}</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('shareModal.vipSubject')}</label>
        <input
          type="text"
          value={vipSubject}
          onChange={(e) => setVipSubject(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          disabled={loading}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('shareModal.vipPreheader')}</label>
        <input
          type="text"
          value={vipPreheader}
          onChange={(e) => setVipPreheader(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          disabled={loading}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('shareModal.vipIntro')}</label>
        <textarea
          value={vipIntro}
          onChange={(e) => setVipIntro(e.target.value)}
          rows={2}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          disabled={loading}
        />
      </div>
      <div className="space-y-2 border rounded-lg p-3 bg-gray-50">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={vipIncludeDescription} onChange={(e) => setVipIncludeDescription(e.target.checked)} />
          {t('shareModal.vipIncludeDescription')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={vipIncludeImage} onChange={(e) => setVipIncludeImage(e.target.checked)} />
          {t('shareModal.vipIncludeImage')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={vipIncludeMetrics} onChange={(e) => setVipIncludeMetrics(e.target.checked)} />
          {t('shareModal.vipIncludeMetrics')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={vipIncludeScenario} onChange={(e) => setVipIncludeScenario(e.target.checked)} />
          {t('shareModal.vipIncludeScenario')}
        </label>
        {vipIncludeScenario && scenarios.length > 0 && (
          <div className="pt-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('shareModal.vipScenarioLabel')}</label>
            <select
              value={vipScenarioId}
              onChange={(e) => setVipScenarioId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
            >
              <option value="">—</option>
              {scenarios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title || s.name || s.id}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handleVipSend}
        disabled={loading || recipients.length === 0}
        className="w-full py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 font-medium"
      >
        {loading ? '…' : t('shareModal.vipSendButton')}
      </button>
    </div>
  );
}
